import { Alert } from '@opendate-app/setlist';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const SeatSelectionSummary = ({ groups, offMapLines, hideTotals, allInPricing, showFeeBreakdown, reserving, blockedReasons = [], onChangeQty, onRemove, onReserve, onBack }) => {
  const [expanded, setExpanded] = useState(false);

  const lines = hideTotals ? [] : offMapLines;
  const totals = [...groups, ...lines].reduce((acc, g) => {
    const units = g.table ? 1 : g.qty;
    acc.subtotal += Number(g.unitPrice) * units;
    acc.face += (g.facePrice == null ? Number(g.unitPrice) : Number(g.facePrice)) * units;
    acc.count += units;
    return acc;
  }, { subtotal: 0, face: 0, count: 0 });

  const feeTotal = Math.max(0, totals.subtotal - totals.face);
  const canBreakdown = allInPricing && showFeeBreakdown && feeTotal > 0;

  return (
    <div className="seat-detail seat-summary">
      <div className="seat-detail-header">
        <div className="seat-detail-title">Your selection</div>
        <button type="button" className="seat-detail-close" aria-label="Back to all listings" onClick={onBack}>×</button>
      </div>
      <div className="seat-summary-groups">
        {groups.map((g) => {
          const units = g.table ? 1 : g.qty;
          const step = g.step || 1;
          const stepUp = g.stepUp || step;
          return (
            <div className="seat-summary-group" key={g.id}>
              <span className="seat-summary-dot" style={{ background: g.color }} aria-hidden="true" />
              <div className="seat-summary-group-info">
                <div className="seat-summary-group-name">{g.name}</div>
                <div className="seat-summary-group-unit">
                  {g.originalUnitPrice != null ? <s className="mr-1">{money(Number(g.originalUnitPrice))}</s> : null}
                  {`${money(Number(g.unitPrice))} each`}
                </div>
                {g.description ? (
                  <div className="seat-summary-group-unit" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(g.description) }} />
                ) : null}
              </div>
              {g.table ? (
                <div className="seat-detail-table-note">1 table</div>
              ) : (
                <div className="seat-detail-stepper">
                  <button type="button" aria-label={`Fewer ${g.name} tickets`} disabled={g.qty <= step} onClick={() => onChangeQty(g.group, g.qty - step)}>−</button>
                  <span className="seat-detail-stepper-value">{g.qty}</span>
                  <button type="button" aria-label={`More ${g.name} tickets`} disabled={g.qty + stepUp > g.limit} onClick={() => onChangeQty(g.group, g.qty + stepUp)}>+</button>
                </div>
              )}
              <div className="seat-summary-group-subtotal">{money(Number(g.unitPrice) * units)}</div>
              <button type="button" className="seat-summary-remove" aria-label={`Remove ${g.name}`} onClick={() => onRemove(g.group)}>×</button>
            </div>
          );
        })}
        {lines.map((line) => (
          <div className="seat-summary-group" key={`off-map-${line.id}`}>
            <div className="seat-summary-group-info">
              <div className="seat-summary-group-name">{line.name}</div>
              <div className="seat-summary-group-unit">
                {line.originalUnitPrice != null ? <s className="mr-1">{money(Number(line.originalUnitPrice))}</s> : null}
                {`${money(Number(line.unitPrice))} each`}
              </div>
            </div>
            <div className="seat-detail-table-note">{`${line.qty} ${line.qty === 1 ? 'ticket' : 'tickets'}`}</div>
            <div className="seat-summary-group-subtotal">{money(Number(line.unitPrice) * line.qty)}</div>
          </div>
        ))}
      </div>
      <div className="seat-detail-footer">
        {blockedReasons.map((reason) => (
          <Alert key={reason} className="mb-2" color="error" role="alert" title={reason} />
        ))}
        {hideTotals ? null : (
          <>
            <div className="seat-detail-subtotal-row">
              <div>
                <div className="seat-detail-subtotal-label">Subtotal</div>
                <div className="seat-detail-subtotal-sub">{`${totals.count} ${totals.count === 1 ? 'ticket' : 'tickets'} · ${allInPricing ? 'fees included' : 'before fees'}`}</div>
              </div>
              <div className="seat-detail-subtotal-right">
                <div className="seat-detail-subtotal-amount">{money(totals.subtotal)}</div>
                {canBreakdown ? (
                  <button
                    type="button"
                    className="seat-detail-toggle"
                    aria-label={expanded ? 'Hide price breakdown' : 'Show price breakdown'}
                    aria-expanded={expanded}
                    onClick={() => setExpanded((prev) => !prev)}
                  >
                    {expanded ? '▴' : '▾'}
                  </button>
                ) : null}
              </div>
            </div>
            {canBreakdown && expanded ? (
              <div className="seat-detail-breakdown">
                <div className="seat-detail-breakdown-row">
                  <span>Face Value</span>
                  <span>{money(totals.face)}</span>
                </div>
                <div className="seat-detail-breakdown-row">
                  <span>Service Fee</span>
                  <span>{money(feeTotal)}</span>
                </div>
              </div>
            ) : null}
            <button type="button" className="seat-detail-reserve btn btn-danger" disabled={reserving || groups.length === 0 || blockedReasons.length > 0} onClick={onReserve}>
              {reserving ? 'Reserving…' : 'Reserve Tickets'}
            </button>
          </>
        )}
        <button type="button" className="seat-detail-back" onClick={onBack}>Back to all listings</button>
      </div>
    </div>
  );
};

SeatSelectionSummary.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      group: PropTypes.object.isRequired,
      name: PropTypes.string,
      qty: PropTypes.number.isRequired,
      limit: PropTypes.number.isRequired,
      step: PropTypes.number,
      stepUp: PropTypes.number,
      unitPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      originalUnitPrice: PropTypes.number,
      facePrice: PropTypes.number,
      color: PropTypes.string,
      table: PropTypes.bool,
      description: PropTypes.string,
    })
  ).isRequired,
  offMapLines: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string,
      qty: PropTypes.number.isRequired,
      unitPrice: PropTypes.number,
      originalUnitPrice: PropTypes.number,
      facePrice: PropTypes.number,
    })
  ),
  hideTotals: PropTypes.bool,
  allInPricing: PropTypes.bool,
  showFeeBreakdown: PropTypes.bool,
  reserving: PropTypes.bool,
  blockedReasons: PropTypes.arrayOf(PropTypes.string),
  onChangeQty: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onReserve: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

SeatSelectionSummary.defaultProps = {
  offMapLines: [],
  hideTotals: false,
};

export default SeatSelectionSummary;
